import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useGeneratedTextPaginatedList } from "../../../hooks/useGeneratedTextPaginatedList";
import { GenerateTextCard } from "../../../components/GenerateTextCard";

import { VocabularyPreviewCard } from "../../../components/VocabularyPreviewCard";
import { ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useStreamingTextIntegrator } from "../../../hooks/useStreamingTextIntegrator";
import { useUserSettings } from "../../../hooks/useUserSettings";
import { useLoginStatus } from "../../../hooks/useLoginStatus";
import { useUserSettingsUpdate } from "../../../hooks/useUserSettingsUpdate";
import { ReepriseTextCard } from "../../../components/ReepriseTextCard";
import { QuickTranslateCard } from "../../../components/QuickTranslateCard";
import {
  useNotifications,
  useMarkNotificationAsRead,
} from "../../../hooks/useNotifications";
import useTranslation from "../../../hooks/useTranslation";
import type { GeneratedText } from "../../../hooks/types/GeneratedTexts";
import { useLanguageSpace } from "../../../hooks/useLanguageSpace";
import { useDailyTextCreate } from "../../../hooks/useDailyTextCreate";
import { REEPRISE_LIST_QUERY_KEY } from "../../../hooks/useGeneratedTextPaginatedList";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import {
  useDailyWeatherTheme,
  type DaylightPhase,
  type DailyWeatherTheme,
} from "../../../hooks/useDailyWeatherTheme";
import { hasTodayStrugglePracticeText } from "../../../utils/hasTodayStrugglePracticeText";
import {
  getLocalIsoDate,
  getLocalIsoDateFromTimestamp,
} from "../../../utils/getLocalIsoDate";

const DESKTOP_UTILITY_MEDIA_QUERY = "(min-width: 1280px)";

function useDesktopUtilityLayout() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia(DESKTOP_UTILITY_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mediaQuery = window.matchMedia(DESKTOP_UTILITY_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return isDesktop;
}

const DAILY_TEXT_DONE_MARKER_KEY = "dailyTextDoneByLanguage";
type DailyDoneMarker = string | { date: string; textId?: number };

function getDoneMarkerDate(marker: DailyDoneMarker | undefined) {
  if (!marker) {
    return undefined;
  }
  return typeof marker === "string" ? marker : marker.date;
}

function getDoneMarkerTextId(marker: DailyDoneMarker | undefined) {
  if (!marker || typeof marker === "string") {
    return undefined;
  }
  return marker.textId;
}

function upsertDoneMarker(
  markers: Record<string, DailyDoneMarker>,
  key: string,
  nextMarker: { date: string; textId?: number },
) {
  const currentMarker = markers[key];
  if (
    typeof currentMarker !== "string" &&
    currentMarker?.date === nextMarker.date &&
    currentMarker?.textId === nextMarker.textId
  ) {
    return markers;
  }

  return {
    ...markers,
    [key]: nextMarker,
  };
}

function parseAiTitleForDailyCard(raw: string): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();

  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (
        parsed &&
        typeof parsed === "object" &&
        typeof parsed.title === "string"
      ) {
        const normalized = parsed.title.trim();
        return normalized || null;
      }
    } catch {
      // fall through
    }
  }

  const unquoted =
    trimmed.startsWith('"') && trimmed.endsWith('"')
      ? trimmed.slice(1, -1).replace(/\\n/g, "\n").replace(/\\"/g, '"')
      : trimmed;

  const titleLineMatch = unquoted.match(/^(?:title|titel)\s*:\s*(.+)$/im);
  if (titleLineMatch?.[1]) {
    const value = titleLineMatch[1].trim();
    return value || null;
  }

  return null;
}

function parseStoryBodyForDailyCard(raw: string): string {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object") {
        if (typeof parsed.story === "string") return parsed.story;
        if (typeof parsed.content === "string") return parsed.content;
      }
    } catch {
      // fall through
    }
  }

  const unquoted =
    trimmed.startsWith('"') && trimmed.endsWith('"')
      ? trimmed.slice(1, -1).replace(/\\n/g, "\n").replace(/\\"/g, '"')
      : trimmed;

  const withoutTitleLine = unquoted.replace(
    /^(Titre|Title|Titel)\s*:\s.*\n+/i,
    "",
  );
  // If content still looks like raw JSON payload, suppress it from UI teaser copy.
  if (/^\s*\{[\s\S]*\}\s*$/.test(withoutTitleLine)) {
    return "";
  }
  return withoutTitleLine;
}

function firstSentencePreviewForDailyCard(
  raw: string,
  maxChars = 110,
): string | null {
  const text = parseStoryBodyForDailyCard(raw)
    .replace(/\s+/g, " ")
    .replace(/^["'`([{<\s]+/, "")
    .replace(/^(story|content)\s*:\s*/i, "")
    .trim();
  if (!text) {
    return null;
  }

  const sentenceMatch = text.match(/.+?[.!?](?:\s|$)/);
  const firstSentence = (sentenceMatch ? sentenceMatch[0] : text)
    .replace(/\s+/g, " ")
    .trim();
  if (firstSentence.length <= maxChars) {
    return firstSentence;
  }
  const clipped = firstSentence.slice(0, maxChars);
  const boundary = clipped.lastIndexOf(" ");
  const safeClip = (
    boundary > 40 ? clipped.slice(0, boundary) : clipped
  ).trimEnd();
  return `${safeClip}...`;
}

function compactDailyDoneTitle(
  title: string | null,
  maxChars = 68,
): string | null {
  if (!title) {
    return null;
  }
  const cleaned = title
    .replace(/\s+/g, " ")
    .replace(/^["'`([{<\s]+/, "")
    .trim();
  if (!cleaned) {
    return null;
  }
  if (cleaned.length <= maxChars) {
    return cleaned;
  }
  const clipped = cleaned.slice(0, maxChars);
  const boundary = clipped.lastIndexOf(" ");
  const safeClip = (
    boundary > 28 ? clipped.slice(0, boundary) : clipped
  ).trimEnd();
  return `${safeClip}...`;
}

function isDailyNudgeText(text: GeneratedText) {
  const topicLower = (text.topic || "").toLowerCase();
  return (
    text.origin === "DAILY_NUDGE" ||
    topicLower.startsWith("daily learning text")
  );
}

function getDaylightMoodOverlay(daylightPhase: DaylightPhase) {
  if (daylightPhase === "dawn") {
    return "absolute inset-0 bg-[linear-gradient(180deg,rgba(255,220,190,0.26)_0%,rgba(255,240,218,0.16)_36%,rgba(255,255,255,0)_78%)]";
  }
  if (daylightPhase === "dusk") {
    return "absolute inset-0 bg-[linear-gradient(180deg,rgba(255,188,148,0.18)_0%,rgba(255,168,130,0.2)_44%,rgba(106,92,170,0.2)_100%)]";
  }
  if (daylightPhase === "night") {
    return "absolute inset-0 bg-[linear-gradient(180deg,rgba(17,24,39,0.34)_0%,rgba(15,23,42,0.42)_100%)]";
  }
  return "";
}

function getDailyCardThemeClasses(
  theme: DailyWeatherTheme,
  daylightPhase: DaylightPhase,
) {
  const moodOverlay = getDaylightMoodOverlay(daylightPhase);
  const base = {
    container: "relative mb-4 overflow-hidden rounded-2xl border p-4 sm:p-5",
    cloudLeft:
      "daily-cloud daily-cloud-a absolute -top-6 left-4 h-14 w-24 rounded-full border bg-[radial-gradient(circle_at_30%_30%,#ffffff_0%,#f4fbff_55%,#d9efff_100%)]",
    cloudLeftMid:
      "daily-cloud daily-cloud-b absolute top-1 left-14 h-12 w-20 rounded-full border bg-[radial-gradient(circle_at_30%_30%,#ffffff_0%,#f5fbff_60%,#deefff_100%)]",
    cloudLeftSmall:
      "daily-cloud daily-cloud-c absolute top-0 left-24 h-11 w-16 rounded-full border bg-[radial-gradient(circle_at_30%_30%,#ffffff_0%,#f3f9ff_58%,#d9ecff_100%)]",
    cloudRight:
      "daily-cloud daily-cloud-d absolute top-7 right-6 h-12 w-20 rounded-full border bg-[radial-gradient(circle_at_30%_30%,#ffffff_0%,#f3faff_58%,#d8ecff_100%)]",
    cloudRightMid:
      "daily-cloud daily-cloud-e absolute top-4 right-14 h-10 w-14 rounded-full border bg-[radial-gradient(circle_at_30%_30%,#ffffff_0%,#f6fbff_60%,#deefff_100%)]",
    cloudRightSmall:
      "daily-cloud daily-cloud-f absolute top-8 right-20 h-9 w-12 rounded-full border bg-[radial-gradient(circle_at_30%_30%,#ffffff_0%,#f6fbff_60%,#deefff_100%)]",
    badge:
      "inline-flex items-center rounded-full border bg-white/80 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]",
    date: "font-serif text-xs font-semibold",
    heading: "font-serif text-lg leading-tight sm:text-xl",
    body: "mt-2 text-sm leading-relaxed",
    action:
      "inline-flex items-center gap-1 self-start text-[11px] font-semibold uppercase tracking-[0.08em] transition disabled:cursor-not-allowed disabled:opacity-50",
  };

  if (theme === "night") {
    return {
      ...base,
      container: `${base.container} border-slate-600 bg-[linear-gradient(180deg,#0f172a_0%,#1e293b_55%,#334155_100%)]`,
      cloudOpacity: "opacity-25",
      cloudBorder: "border-slate-300/30",
      badgeText: `${base.badge} border-slate-400/40 text-slate-100`,
      dateText: `${base.date} text-slate-100`,
      headingText: `${base.heading} text-slate-50`,
      bodyText: `${base.body} text-slate-200`,
      actionText: `${base.action} text-slate-100 hover:text-white`,
      weatherOverlay:
        "absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(148,163,184,0.2),transparent_45%)]",
      moodOverlay,
    };
  }
  if (theme === "snow") {
    return {
      ...base,
      container: `${base.container} border-cyan-200 bg-[linear-gradient(180deg,#dff5ff_0%,#eef8ff_58%,#f8fcff_100%)]`,
      cloudOpacity: "opacity-70",
      cloudBorder: "border-white/80",
      badgeText: `${base.badge} border-cyan-200 text-cyan-700`,
      dateText: `${base.date} text-cyan-900`,
      headingText: `${base.heading} text-cyan-950`,
      bodyText: `${base.body} text-cyan-900/85`,
      actionText: `${base.action} text-cyan-700 hover:text-cyan-800`,
      weatherOverlay:
        "absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,255,255,0.55),transparent_35%)]",
      moodOverlay,
    };
  }
  if (theme === "rain") {
    return {
      ...base,
      container: `${base.container} border-blue-300 bg-[linear-gradient(180deg,#7ba8cf_0%,#8fb2d0_45%,#b6cbe0_100%)]`,
      cloudOpacity: "opacity-45",
      cloudBorder: "border-white/45",
      badgeText: `${base.badge} border-blue-300 text-blue-800`,
      dateText: `${base.date} text-blue-950`,
      headingText: `${base.heading} text-blue-950`,
      bodyText: `${base.body} text-blue-950/80`,
      actionText: `${base.action} text-blue-800 hover:text-blue-900`,
      weatherOverlay:
        "absolute inset-0 bg-[repeating-linear-gradient(105deg,rgba(255,255,255,0.14)_0px,rgba(255,255,255,0.14)_1px,transparent_1px,transparent_9px)] opacity-45",
      moodOverlay,
    };
  }
  if (theme === "cloudy") {
    return {
      ...base,
      container: `${base.container} border-slate-300 bg-[linear-gradient(180deg,#b9cad7_0%,#c8d7e3_52%,#dce7ef_100%)]`,
      cloudOpacity: "opacity-85",
      cloudBorder: "border-white/55",
      badgeText: `${base.badge} border-slate-300 text-slate-700`,
      dateText: `${base.date} text-slate-800`,
      headingText: `${base.heading} text-slate-900`,
      bodyText: `${base.body} text-slate-800/85`,
      actionText: `${base.action} text-slate-700 hover:text-slate-900`,
      weatherOverlay:
        "absolute inset-0 bg-[radial-gradient(circle_at_25%_10%,rgba(255,255,255,0.3),transparent_40%)]",
      moodOverlay,
    };
  }
  if (theme === "clear") {
    return {
      ...base,
      container: `${base.container} border-sky-200 bg-[linear-gradient(180deg,#8fd3ff_0%,#bde7ff_36%,#dff3ff_72%,#f3faff_100%)]`,
      cloudOpacity: "opacity-95",
      cloudBorder: "border-white/70",
      badgeText: `${base.badge} border-sky-200 text-sky-700`,
      dateText: `${base.date} text-sky-900`,
      headingText: `${base.heading} text-sky-950`,
      bodyText: `${base.body} text-sky-900/80`,
      actionText: `${base.action} text-sky-700/90 hover:text-sky-800`,
      weatherOverlay: "",
      moodOverlay,
    };
  }
  return {
    ...base,
    container: `${base.container} border-slate-200 bg-[linear-gradient(180deg,#eef4fb_0%,#f4f8fc_65%,#f8fafc_100%)]`,
    cloudOpacity: "opacity-70",
    cloudBorder: "border-white/70",
    badgeText: `${base.badge} border-slate-200 text-slate-700`,
    dateText: `${base.date} text-slate-800`,
    headingText: `${base.heading} text-slate-900`,
    bodyText: `${base.body} text-slate-700`,
    actionText: `${base.action} text-slate-700 hover:text-slate-900`,
    weatherOverlay: "",
    moodOverlay,
  };
}

export const Route = createFileRoute("/app/reeprise/")({
  component: Reeprise,
});

function Reeprise() {
  const { translate } = useTranslation();
  const { languageSpace } = useLanguageSpace("ENGLISH");
  const isDesktopUtilityLayout = useDesktopUtilityLayout();
  const paginatedGeneratedTextList = useGeneratedTextPaginatedList();
  const [showAllDailyHistoryDesktop, setShowAllDailyHistoryDesktop] =
    useState(false);
  const [dailyHistoryMeta, setDailyHistoryMeta] = useState({
    hasMore: false,
    hiddenCount: 0,
  });
  const todayIso = getLocalIsoDate();

  const userSettingsQuery = useUserSettings();

  //HACK this establishes the WS connection
  useStreamingTextIntegrator({ textId: 0 });

  useEffect(() => {
    setShowAllDailyHistoryDesktop(false);
  }, [languageSpace, todayIso]);

  if (!paginatedGeneratedTextList.isSuccess || !userSettingsQuery.isSuccess) {
    return <div>Loading</div>;
  }

  const visibleTexts = paginatedGeneratedTextList.data.pages
    .flatMap((page) => page.results)
    .filter((text) =>
      languageSpace === "ALL" ? true : text.language === languageSpace,
    );
  const hasTodayStruggleText = hasTodayStrugglePracticeText({
    texts: visibleTexts,
    todayIso,
    languageSpace,
  });

  return (
    <div className="min-h-screen bg-neutral-50 px-2 pt-4 pb-4 sm:px-3 sm:pt-5 lg:px-4 xl:px-6 xl:pb-44">
      <div className="mx-auto w-full max-w-[1100px]">
        {userSettingsQuery.data.show_welcome_card && <WelcomeCard />}
        <NotificationsCenter />

        <div className="grid w-full gap-8 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] xl:items-start">
          <div
            className="min-w-0 space-y-6 xl:space-y-7"
            data-testid="reeprise-main-column"
          >
            {!isDesktopUtilityLayout && (
              <div className="space-y-4" data-testid="reeprise-utility-row-mobile">
                <VocabularyPreviewCard />
                <QuickTranslateCard />
              </div>
            )}

            <div className="xl:hidden" data-testid="reeprise-mobile-compose-shell">
              <div className="mx-auto w-full md:max-w-[calc(100vw-var(--app-desktop-nav-width,72px)-1.5rem)]">
                <GenerateTextCard showStruggleBanner={!hasTodayStruggleText} />
              </div>
            </div>

            <section className="space-y-3" data-testid="reeprise-daily-section">
              <div className="grid items-stretch gap-4 lg:grid-cols-3">
                <DailyPracticeCallout
                  generatedTexts={visibleTexts}
                  languageSpace={languageSpace}
                  layout="desktopStrip"
                  showAllHistoryDesktop={showAllDailyHistoryDesktop}
                  onDesktopHistoryMetaChange={setDailyHistoryMeta}
                />
              </div>
              {dailyHistoryMeta.hasMore ? (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowAllDailyHistoryDesktop((prev) => !prev)}
                    className="inline-flex cursor-pointer items-center py-1 text-xs font-medium text-slate-500 transition-colors hover:text-slate-800"
                  >
                    {showAllDailyHistoryDesktop
                      ? translate("reeprise_daily_show_less")
                      : translate("reeprise_daily_show_more", {
                          count: dailyHistoryMeta.hiddenCount,
                        })}
                  </button>
                </div>
              ) : null}
            </section>

            <section className="space-y-4">
              <h2 className="font-serif text-[1.15rem] font-semibold leading-tight text-slate-900 sm:text-[1.2rem]">
                {translate("reeprise_feed_section_title")}
              </h2>
              <ul
                className="grid w-full grid-cols-1 gap-3 self-start"
                data-testid="reeprise-feed-list"
              >
                {sortGeneratedTexts(visibleTexts).map((generatedText) => {
                  return (
                    <ReepriseTextCard
                      key={generatedText.id}
                      generatedText={generatedText}
                    />
                  );
                })}
                {paginatedGeneratedTextList.hasNextPage && (
                  <li className="mb-1 w-full">
                    <button
                      className="w-full rounded-xl border border-slate-200 bg-neutral-50 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-900"
                      onClick={() => {
                        paginatedGeneratedTextList.fetchNextPage();
                      }}
                    >
                      Load more
                    </button>
                  </li>
                )}
              </ul>
            </section>
          </div>

          {isDesktopUtilityLayout && (
            <aside
              className="hide-scrollbar min-w-0 space-y-4 xl:sticky xl:top-0 xl:max-h-[calc(100dvh-6rem)] xl:overflow-y-auto xl:pr-1 xl:pb-44"
              data-testid="reeprise-utility-row"
            >
              <VocabularyPreviewCard />
              <QuickTranslateCard />
            </aside>
          )}
        </div>
      </div>

      <div
        className="pointer-events-none fixed bottom-4 z-20 hidden w-full max-w-[1100px] -translate-x-1/2 px-2 sm:px-3 lg:px-4 xl:block xl:w-[calc(100vw-var(--app-desktop-nav-width,72px))] xl:left-[calc((100vw+var(--app-desktop-nav-width,72px))/2)]"
        data-testid="reeprise-desktop-compose"
      >
        <div className="grid w-full grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6">
          <div />
          <div />
        </div>
        <div className="mt-3 flex justify-center">
          <div className="pointer-events-auto w-full max-w-[720px]">
            <GenerateTextCard showStruggleBanner={!hasTodayStruggleText} />
          </div>
        </div>
      </div>
    </div>
  );
}

function DailyPracticeCallout({
  generatedTexts,
  languageSpace,
  layout = "default",
  showAllHistoryDesktop = false,
  onDesktopHistoryMetaChange,
}: {
  generatedTexts: GeneratedText[];
  languageSpace: string;
  layout?: "default" | "desktopStrip";
  showAllHistoryDesktop?: boolean;
  onDesktopHistoryMetaChange?: (meta: {
    hasMore: boolean;
    hiddenCount: number;
  }) => void;
}) {
  const isDesktopStrip = layout === "desktopStrip";
  const doneListCardClass = isDesktopStrip
    ? "h-full cursor-pointer rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5"
    : "mb-3 cursor-pointer rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3";
  const desktopHistoryPreviewLimit = 2;
  const { translate, language: uiLanguage } = useTranslation();
  const navigate = useNavigate();
  const mutation = useDailyTextCreate();
  const queryClient = useQueryClient();
  const [createdTodayByLanguage, setCreatedTodayByLanguage] = useLocalStorage<
    Record<string, DailyDoneMarker>
  >(DAILY_TEXT_DONE_MARKER_KEY, {});

  const now = new Date();
  const isMorningOrLater = now.getHours() >= 6;
  const weatherThemeQuery = useDailyWeatherTheme({ enabled: isMorningOrLater });
  const todayIso = getLocalIsoDate(now);
  const locale = uiLanguage === "GERMAN" ? "de-DE" : "en-US";
  const weekdayLabel = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: "long" }).format(now),
    [locale, todayIso],
  );
  const dateLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
      }).format(now),
    [locale, todayIso],
  );

  // Reset mutation state when switching language or day to avoid cross-language success messages.
  useEffect(() => {
    mutation.reset();
  }, [languageSpace, todayIso]);

  const resolvedLanguage = languageSpace === "ALL" ? "ENGLISH" : languageSpace;
  const markerForLanguageSpace = createdTodayByLanguage[languageSpace];
  const markerForResolvedLanguage = createdTodayByLanguage[resolvedLanguage];
  const markerDateForLanguageSpace = getDoneMarkerDate(markerForLanguageSpace);
  const markerDateForResolvedLanguage = getDoneMarkerDate(
    markerForResolvedLanguage,
  );

  // If we stored a local creation marker for this language+day, keep collapsed state stable
  // even if list refetch lags or the route remounts.
  const locallyCreatedToday =
    markerDateForLanguageSpace === todayIso ||
    markerDateForResolvedLanguage === todayIso;

  if (!isMorningOrLater) {
    return null;
  }

  const todayPracticeText = generatedTexts.find((text) => {
    const createdDate = getLocalIsoDateFromTimestamp(text.created_at);

    return createdDate === todayIso && isDailyNudgeText(text);
  });
  const fallbackTodayPracticeText = generatedTexts.find((text) => {
    const createdDate = getLocalIsoDateFromTimestamp(text.created_at);
    return createdDate === todayIso && isDailyNudgeText(text);
  });
  const dailyPracticeHistoryTexts = useMemo(() => {
    return generatedTexts
      .filter((text) => {
        const createdDate = getLocalIsoDateFromTimestamp(text.created_at);
        return Boolean(createdDate) && isDailyNudgeText(text);
      })
      .toSorted(
        (a, b) =>
          new Date(b.created_at).valueOf() - new Date(a.created_at).valueOf(),
      );
  }, [generatedTexts]);
  const hasTodayPractice = Boolean(
    todayPracticeText || fallbackTodayPracticeText,
  );
  const [doneLatched, setDoneLatched] = useState(
    hasTodayPractice || locallyCreatedToday,
  );

  useEffect(() => {
    // Reset latch for a new day/language bucket.
    setDoneLatched(hasTodayPractice || locallyCreatedToday);
  }, [languageSpace, todayIso]);

  useEffect(() => {
    // Once done is visible, keep it stable through transient query/cache updates.
    if (!doneLatched && (hasTodayPractice || locallyCreatedToday)) {
      setDoneLatched(true);
    }
  }, [doneLatched, hasTodayPractice, locallyCreatedToday]);

  const handleCreate = async () => {
    try {
      const created = await mutation.mutateAsync({
        language: resolvedLanguage,
      });
      setCreatedTodayByLanguage((prev) => {
        const nextMarker = { date: todayIso, textId: created?.id };
        const withLanguageSpace = upsertDoneMarker(
          prev,
          languageSpace,
          nextMarker,
        );
        return upsertDoneMarker(
          withLanguageSpace,
          resolvedLanguage,
          nextMarker,
        );
      });
      void queryClient.invalidateQueries({
        queryKey: [REEPRISE_LIST_QUERY_KEY],
      });
      if (created?.id) {
        navigate({
          to: "/app/reeprise/$generatedTextId",
          params: { generatedTextId: `${created.id}` },
          search: {
            chapter: undefined,
            focusWord: undefined,
            focusSentence: undefined,
            focusToken: undefined,
          },
        });
      }
    } catch (err) {
      // handled by mutation error state
    }
  };

  const disabled = mutation.isPending;
  const markerTextIdForToday =
    markerDateForLanguageSpace === todayIso
      ? getDoneMarkerTextId(markerForLanguageSpace)
      : markerDateForResolvedLanguage === todayIso
        ? getDoneMarkerTextId(markerForResolvedLanguage)
        : undefined;
  const openTextId =
    todayPracticeText?.id ??
    fallbackTodayPracticeText?.id ??
    markerTextIdForToday;
  const canOpenDoneCard = Boolean(openTextId);
  const doneCardText =
    (openTextId
      ? generatedTexts.find((text) => text.id === openTextId)
      : undefined) ??
    todayPracticeText ??
    fallbackTodayPracticeText;
  const doneCardTitle = useMemo(() => {
    if (!doneCardText) {
      return null;
    }
    const raw =
      doneCardText.status === "COMPLETE"
        ? doneCardText.chatgpt_output || ""
        : "";
    const resolved =
      parseAiTitleForDailyCard(raw) || doneCardText.topic || null;
    return compactDailyDoneTitle(resolved);
  }, [doneCardText]);
  const doneCardDescription = useMemo(() => {
    if (!doneCardText || doneCardText.status !== "COMPLETE") {
      return translate("dailyTextDoneDescription");
    }
    return (
      firstSentencePreviewForDailyCard(doneCardText.chatgpt_output || "") ||
      translate("dailyTextDoneDescription")
    );
  }, [doneCardText, translate]);
  const weatherTheme: DailyWeatherTheme =
    weatherThemeQuery.data?.theme ?? "neutral";
  const daylightPhase: DaylightPhase =
    weatherThemeQuery.data?.daylight_phase ??
    (weatherTheme === "night" ? "night" : "day");
  const activeThemeClasses = getDailyCardThemeClasses(
    weatherTheme,
    daylightPhase,
  );
  const doneCardDateIso = getLocalIsoDateFromTimestamp(
    doneCardText?.created_at,
  );
  const isDoneCardForToday =
    doneCardDateIso === todayIso || locallyCreatedToday;
  const doneThemeClasses = isDoneCardForToday
    ? activeThemeClasses
    : getDailyCardThemeClasses("neutral", "day");
  const featuredDailyTextId = doneLatched ? doneCardText?.id : undefined;
  const neutralDailyHistoryTexts = dailyPracticeHistoryTexts.filter(
    (text) => text.id !== featuredDailyTextId,
  );
  const desktopGeneratedHistoryTexts = useMemo(() => {
    return sortGeneratedTexts(generatedTexts).filter(
      (text) => text.id !== featuredDailyTextId,
    );
  }, [generatedTexts, featuredDailyTextId]);

  useEffect(() => {
    if (!openTextId) {
      return;
    }
    const markerHasTextIdForToday =
      (markerDateForLanguageSpace === todayIso &&
        getDoneMarkerTextId(markerForLanguageSpace) === openTextId) ||
      (markerDateForResolvedLanguage === todayIso &&
        getDoneMarkerTextId(markerForResolvedLanguage) === openTextId);
    if (markerHasTextIdForToday) {
      return;
    }
    setCreatedTodayByLanguage((prev) => {
      const nextMarker = { date: todayIso, textId: openTextId };
      const withLanguageSpace = upsertDoneMarker(
        prev,
        languageSpace,
        nextMarker,
      );
      return upsertDoneMarker(withLanguageSpace, resolvedLanguage, nextMarker);
    });
  }, [
    openTextId,
    markerDateForLanguageSpace,
    markerDateForResolvedLanguage,
    markerForLanguageSpace,
    markerForResolvedLanguage,
    todayIso,
    languageSpace,
    resolvedLanguage,
    setCreatedTodayByLanguage,
  ]);
  const doneContainerClass = `${doneThemeClasses.container.replace("mb-4", "mb-0")} ${isDesktopStrip ? "h-full !p-3" : ""} ${canOpenDoneCard ? "cursor-pointer hover:cursor-pointer" : ""}`;
  const createContainerClass = `${activeThemeClasses.container.replace("mb-4", "mb-0")} ${isDesktopStrip ? "h-full !p-3" : ""} ${disabled ? "cursor-not-allowed" : "cursor-pointer hover:cursor-pointer"}`;
  const historyPool = isDesktopStrip
    ? desktopGeneratedHistoryTexts
    : neutralDailyHistoryTexts;
  const visibleDailyHistoryTexts =
    isDesktopStrip && !showAllHistoryDesktop
      ? historyPool.slice(0, desktopHistoryPreviewLimit)
      : historyPool;
  const hiddenHistoryCount =
    historyPool.length - visibleDailyHistoryTexts.length;

  useEffect(() => {
    if (!isDesktopStrip || !onDesktopHistoryMetaChange) {
      return;
    }
    onDesktopHistoryMetaChange({
      hasMore: historyPool.length > desktopHistoryPreviewLimit,
      hiddenCount: Math.max(0, hiddenHistoryCount),
    });
  }, [
    isDesktopStrip,
    onDesktopHistoryMetaChange,
    historyPool.length,
    hiddenHistoryCount,
    desktopHistoryPreviewLimit,
  ]);

  const historyCards = visibleDailyHistoryTexts.map((text) => {
    const createdAt = new Date(text.created_at);
    const compactDate = new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
    }).format(createdAt);
    const raw = text.status === "COMPLETE" ? text.chatgpt_output || "" : "";
    const cardTitle = compactDailyDoneTitle(
      parseAiTitleForDailyCard(raw) || text.topic || null,
    );
    const cardDescription =
      text.status === "COMPLETE"
        ? firstSentencePreviewForDailyCard(text.chatgpt_output || "") ||
          translate("dailyTextDoneDescription")
        : translate("dailyTextDoneDescription");

    return (
      <div
        key={`daily-history-${text.id}`}
        data-testid="reeprise-daily-history-card"
        className={doneListCardClass}
        role="button"
        tabIndex={0}
        onClick={() =>
          navigate({
            to: "/app/reeprise/$generatedTextId",
            params: { generatedTextId: `${text.id}` },
            search: {
              chapter: undefined,
              focusWord: undefined,
              focusSentence: undefined,
              focusToken: undefined,
            },
          })
        }
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            navigate({
              to: "/app/reeprise/$generatedTextId",
              params: { generatedTextId: `${text.id}` },
              search: {
                chapter: undefined,
                focusWord: undefined,
                focusSentence: undefined,
                focusToken: undefined,
              },
            });
          }
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-600">
            {translate("dailyTextPreviousBadge")}
          </p>
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">
            {compactDate}
          </p>
        </div>
        <p
          className={`mt-2 font-serif text-slate-900 ${isDesktopStrip ? "text-sm leading-snug" : "text-base"}`}
        >
          {cardTitle ?? translate("dailyTextDoneHeading")}
        </p>
        <p
          className={`mt-1 text-slate-700 ${isDesktopStrip ? "text-xs leading-snug" : "text-sm"}`}
        >
          {cardDescription}
        </p>
      </div>
    );
  });

  if (doneLatched) {
    return (
      <>
        <div
          data-testid="reeprise-daily-primary-card"
          className={doneContainerClass}
          role={canOpenDoneCard ? "button" : undefined}
          tabIndex={canOpenDoneCard ? 0 : undefined}
          onClick={
            canOpenDoneCard
              ? () =>
                  navigate({
                    to: "/app/reeprise/$generatedTextId",
                    params: { generatedTextId: `${openTextId}` },
                    search: {
                      chapter: undefined,
                      focusWord: undefined,
                      focusSentence: undefined,
                      focusToken: undefined,
                    },
                  })
              : undefined
          }
          onKeyDown={
            canOpenDoneCard
              ? (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate({
                      to: "/app/reeprise/$generatedTextId",
                      params: { generatedTextId: `${openTextId}` },
                      search: {
                        chapter: undefined,
                        focusWord: undefined,
                        focusSentence: undefined,
                        focusToken: undefined,
                      },
                    });
                  }
                }
              : undefined
          }
        >
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 ${doneThemeClasses.cloudOpacity}`}
          >
            <div
              className={`${doneThemeClasses.cloudLeft} ${doneThemeClasses.cloudBorder}`}
            />
            <div
              className={`${doneThemeClasses.cloudLeftMid} ${doneThemeClasses.cloudBorder}`}
            />
            <div
              className={`${doneThemeClasses.cloudLeftSmall} ${doneThemeClasses.cloudBorder}`}
            />
            <div
              className={`${doneThemeClasses.cloudRight} ${doneThemeClasses.cloudBorder}`}
            />
            <div
              className={`${doneThemeClasses.cloudRightMid} ${doneThemeClasses.cloudBorder}`}
            />
            <div
              className={`${doneThemeClasses.cloudRightSmall} ${doneThemeClasses.cloudBorder}`}
            />
            {doneThemeClasses.weatherOverlay ? (
              <div className={doneThemeClasses.weatherOverlay} />
            ) : null}
            {doneThemeClasses.moodOverlay ? (
              <div className={doneThemeClasses.moodOverlay} />
            ) : null}
          </div>
          <div className="relative">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className={doneThemeClasses.badgeText}>{weekdayLabel}</p>
              <p className={doneThemeClasses.dateText}>{dateLabel}</p>
            </div>
            <div
              className={
                isDesktopStrip
                  ? "grid gap-2"
                  : "grid gap-3 md:grid-cols-[1fr_auto] md:items-start"
              }
            >
              <div className="min-w-0">
                <p className={doneThemeClasses.headingText}>
                  {doneCardTitle ?? translate("dailyTextDoneHeading")}
                </p>
                <p
                  className={`${doneThemeClasses.bodyText} ${isDesktopStrip ? "hidden" : ""}`}
                >
                  {doneCardDescription}
                </p>
              </div>
              {canOpenDoneCard ? (
                <p
                  className={`${doneThemeClasses.actionText} ${isDesktopStrip ? "mt-1 text-[10px]" : ""}`}
                >
                  {translate("dailyTextDoneOpen")}
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                </p>
              ) : null}
            </div>
          </div>
        </div>
        {historyCards}
      </>
    );
  }

  return (
    <>
      <div
        data-testid="reeprise-daily-primary-card"
        className={createContainerClass}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : () => void handleCreate()}
        onKeyDown={
          disabled
            ? undefined
            : (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  void handleCreate();
                }
              }
        }
      >
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 ${activeThemeClasses.cloudOpacity}`}
        >
          <div
            className={`${activeThemeClasses.cloudLeft} ${activeThemeClasses.cloudBorder}`}
          />
          <div
            className={`${activeThemeClasses.cloudLeftMid} ${activeThemeClasses.cloudBorder}`}
          />
          <div
            className={`${activeThemeClasses.cloudLeftSmall} ${activeThemeClasses.cloudBorder}`}
          />
          <div
            className={`${activeThemeClasses.cloudRight} ${activeThemeClasses.cloudBorder}`}
          />
          <div
            className={`${activeThemeClasses.cloudRightMid} ${activeThemeClasses.cloudBorder}`}
          />
          <div
            className={`${activeThemeClasses.cloudRightSmall} ${activeThemeClasses.cloudBorder}`}
          />
          {activeThemeClasses.weatherOverlay ? (
            <div className={activeThemeClasses.weatherOverlay} />
          ) : null}
          {activeThemeClasses.moodOverlay ? (
            <div className={activeThemeClasses.moodOverlay} />
          ) : null}
        </div>
        <div className="relative">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className={activeThemeClasses.badgeText}>{weekdayLabel}</p>
            <p className={activeThemeClasses.dateText}>{dateLabel}</p>
          </div>
          <div
            className={
              isDesktopStrip
                ? "grid gap-2"
                : "grid gap-3 md:grid-cols-[1fr_auto] md:items-start"
            }
          >
            <div className="min-w-0">
              <p
                className={`${activeThemeClasses.headingText} ${isDesktopStrip ? "text-base sm:text-base" : ""}`}
              >
                {translate("dailyTextHeading")}
              </p>
              <p
                className={`${activeThemeClasses.bodyText} ${isDesktopStrip ? "hidden" : ""}`}
              >
                {translate("dailyTextDescription")}
              </p>
            </div>
            <button
              onClick={(event) => {
                event.stopPropagation();
                void handleCreate();
              }}
              disabled={disabled}
              className={`${activeThemeClasses.actionText} ${isDesktopStrip ? "mt-1 text-[10px]" : ""}`}
            >
              {mutation.isPending
                ? translate("dailyTextLoading")
                : translate("dailyTextCta")}
              {!mutation.isPending ? (
                <ChevronRightIcon className="h-3.5 w-3.5" />
              ) : null}
            </button>
          </div>
        </div>
        {mutation.isError && (
          <p className="relative mt-4 text-xs text-rose-600">
            {mutation.error?.message ?? translate("dailyTextError")}
          </p>
        )}
        {mutation.isSuccess && (
          <p className="relative mt-4 text-xs font-medium text-emerald-800">
            {translate("dailyTextSuccess")}
          </p>
        )}
      </div>
      {historyCards}
    </>
  );
}

function WelcomeCard() {
  const loginStatus = useLoginStatus();

  const updateUserSettings = useUserSettingsUpdate();

  if (!loginStatus.isSuccess || loginStatus.data.state === "logged out") {
    return <></>;
  }

  return (
    <div className="max-w-2xl bg-white shadow-xl rounded-2xl relative p-8 mb-10 text-center border-2 border-blue-200 hover:border-blue-300">
      <div
        className="block absolute right-4 top-5 cursor-pointer"
        onClick={() => {
          updateUserSettings.mutate({
            userSettingsDto: {
              show_welcome_card: false,
            },
          });
        }}
      >
        <XMarkIcon className="w-8 h-8 mr-1" />
      </div>
      <h1 className="text-3xl font-bold text-[#1e293b] mt-5">
        Hi {loginStatus.data.username}!
      </h1>

      <p className="mt-4 text-[#1e293b]">
        Erstelle in wenigen Schritten deine eigenen Sprachlern-Texte:
      </p>

      <div className="mt-6 text-left space-y-4 text-[#1e293b]">
        <div className="flex items-start space-x-3">
          <span className="text-xl text-[#86efac]">1️⃣</span>
          <p>
            <span className="font-semibold text-[#1e293b]">
              Thema festlegen
            </span>{" "}
            – Wähle, worüber dein Text handeln soll.
          </p>
        </div>

        <div className="flex items-start space-x-3">
          <span className="text-xl text-[#86efac]">2️⃣</span>
          <div>
            <p className="font-semibold text-[#1e293b]">
              Details anpassen – Unter „Weitere Optionen“ kannst du:
            </p>
            <ul className="list-disc list-inside mt-2 text-[#1e293b]">
              <li>Eine Hauptfigur bestimmen (optional)</li>
              <li>Lern-Sprache und Sprach-Level (A0–C2) wählen</li>
              <li>
                Eine Zeitform für deinen Text festlegen (Präsens,Perfekt,...)
              </li>
              <li>Den Text-Typ festlegen (z. B. Geschichte, Dialog)</li>
              <li>Die gewünschte Textlänge anhand der Wortanzahl definieren</li>
            </ul>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <span className="text-xl text-[#86efac]">3️⃣</span>
          <p>
            <span className="font-semibold text-[#1e293b]">
              Lass dich inspirieren!
            </span>{" "}
            – Lade ein Bild hoch, und dein Text wird von dessen Inhalt und
            Stimmung beeinflusst.
          </p>
        </div>
      </div>

      <div
        className="w-full mt-6 bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition duration-200 cursor-pointer"
        onClick={() => {
          updateUserSettings.mutate({
            userSettingsDto: {
              show_welcome_card: false,
            },
          });
        }}
      >
        <p className="font-bold">Jetzt Text erstellen</p>
      </div>
    </div>
  );
}

function NotificationsCenter() {
  const { translate } = useTranslation();
  const notificationsQuery = useNotifications();
  const markRead = useMarkNotificationAsRead();

  if (!notificationsQuery.isSuccess) {
    return null;
  }
  const unreadShares = notificationsQuery.data.filter(
    (notification) =>
      !notification.is_read && notification.notification_type === "TEXT_SHARED",
  );
  if (unreadShares.length === 0) {
    return null;
  }
  return (
    <div className="mb-4 space-y-3">
      {unreadShares.map((notification) => {
        const payload = notification.payload as {
          text_id?: number;
          topic?: string;
          sender_username?: string;
        };
        return (
          <div
            key={notification.id}
            className="rounded-2xl border border-blue-200 bg-blue-50 p-4 shadow-sm"
          >
            <p className="text-sm text-slate-800">
              {translate("notifications_share_message", {
                username: payload.sender_username ?? "Jemand",
                topic:
                  payload.topic ??
                  translate("notifications_share_default_topic"),
              })}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {payload.text_id && (
                <Link
                  to="/app/reeprise/$generatedTextId"
                  params={{ generatedTextId: `${payload.text_id}` }}
                  search={{
                    chapter: undefined,
                    focusWord: undefined,
                    focusSentence: undefined,
                    focusToken: undefined,
                  }}
                  className="rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700"
                >
                  {translate("notifications_share_open")}
                </Link>
              )}
              <button
                type="button"
                onClick={() => markRead.mutate(notification.id)}
                className="rounded-full border border-blue-300 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-white disabled:opacity-50"
                disabled={markRead.isPending}
              >
                {translate("notifications_share_dismiss")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function sortGeneratedTexts(texts: GeneratedText[]) {
  return [...texts].toSorted((a, b) => {
    const aTimestamp = a.shared_from
      ? new Date(a.shared_from.shared_at).valueOf()
      : new Date(a.created_at).valueOf();
    const bTimestamp = b.shared_from
      ? new Date(b.shared_from.shared_at).valueOf()
      : new Date(b.created_at).valueOf();
    if (aTimestamp !== bTimestamp) {
      return bTimestamp - aTimestamp;
    }
    // If timestamps are equal, prefer shared texts first for consistency
    if (Boolean(b.shared_from) !== Boolean(a.shared_from)) {
      return Number(Boolean(b.shared_from)) - Number(Boolean(a.shared_from));
    }
    return 0;
  });
}
